// Globals
import { useTranslation } from 'react-i18next';

// Components
import { Form, FormItem, Modal, ModalSize } from '@/components';
import { Button, Input, Select } from '@/ui';
import WebsiteChannelBlock from '../WebsiteChannelBlock/WebsiteChannelBlock';

// Modules
import { ImageUpload } from '@/modules/Settings';
import { ALLOWED_IMAGE_TYPES } from '@/modules/Categories';

// Hooks
import { useGetRequiredRule } from '@/hooks';

// Models
import { WebsitesStatus } from '@/models/websites.model';

// Helpers
import { WebsiteFieldsNames, WebsiteFormType, TWebsiteFormValues } from '../../helpers';

type TWebsiteFormProps = {
  isOpen: boolean;
  isLoading?: boolean;
  formType: WebsiteFormType;
  initialValues?: Partial<TWebsiteFormValues>;
  onSubmit: (values: TWebsiteFormValues) => void;
  toggleOpen: () => void;
};

const statusOptions = [
  { value: WebsitesStatus.ACTIVE, label: 'Active' },
  { value: WebsitesStatus.INACTIVE, label: 'Inactive' },
];

const WebsiteForm = ({ isOpen, isLoading, formType, initialValues, onSubmit, toggleOpen }: TWebsiteFormProps) => {
  const { t } = useTranslation(['common']);
  const requiredRule = useGetRequiredRule({ t });
  const isUpdate = formType === WebsiteFormType.UPDATE;

  return (
    <Modal
      visible={isOpen}
      title={t(isUpdate ? 'update_website' : 'create_website')}
      destroyOnClose
      onCancel={toggleOpen}
      onClose={toggleOpen}
      size={ModalSize.LARGE}
    >
      <Form onFinish={onSubmit} initialValues={initialValues}>
        <FormItem name={WebsiteFieldsNames.TITLE} rules={[requiredRule]} label={t('label_title')}>
          <Input />
        </FormItem>
        <FormItem name={WebsiteFieldsNames.DESCRIPTION} label={t('label_description')}>
          <Input />
        </FormItem>
        <WebsiteChannelBlock disabled={isUpdate} />
        <FormItem name={WebsiteFieldsNames.IMAGE} rules={isUpdate ? [] : [requiredRule]} label={t('label_image')}>
          <ImageUpload accept={ALLOWED_IMAGE_TYPES.join(',')} />
        </FormItem>
        <FormItem name={WebsiteFieldsNames.IMAGE_TITLE} label={t('label_image_alt')}>
          <Input />
        </FormItem>
        <FormItem name={WebsiteFieldsNames.STATUS} label={t('label_status')}>
          <Select options={statusOptions} placeholder={t('placeholder_status')} />
        </FormItem>
        <Button type="primary" htmlType="submit" block loading={isLoading}>
          {t(isUpdate ? 'update_btn' : 'create_btn')}
        </Button>
      </Form>
    </Modal>
  );
};

export default WebsiteForm;
